import type {Option} from '@/cart/types';
import {Plan} from '@rechargeapps/storefront-client';
import {HasMetafieldsIdentifier} from '@shopify/hydrogen-react/storefront-api-types';
import {z} from 'zod';

export type GetProductInput = {
  id: string;
  variant_metafields: HasMetafieldsIdentifier[];
};

export type GetVariantMetafieldInput = {
  key: string;
  namespace: string;
  productId: number;
  selectedOptions: Option[];
};

export type Image = {
  alt: string;
  url: string;
};

export type Product = ProductMetafields & {
  description: string;
  featuredImage: Image | null;
  handle: string;
  id: string;
  options: ProductOption[];
  requiresSellingPlan: boolean;
  title: string;
  variants: ProductVariant[];
};

export type ProductMetafields = {
  average_rating?: number | null;
  number_of_reviews?: number | null;
  rechargePlans?: Plan[] | null;
};

export type PriceAdjustment = {
  adjustmentValue: PriceAdjustmentValuePercentage;
};

export type PriceAdjustmentValuePercentage = {
  adjustmentPercentage: number;
};

export type ProductOption = {
  name: string;
  values: string[];
};

export type ProductVariant = ProductVariantMetafields & {
  id: string;
  image: Image | null;
  price: number;
  selectedOptions: Option[];
  sellingPlanAllocations: SellingPlanAllocation[];
};

export type ProductVariantMetafields = {
  number_of_loads: number;
  pack_size?: number | null;
  upsell_compare_at_price?: number | null;
};

export type SellingPlan = {
  id: string;
  name: string;
  priceAdjustments: PriceAdjustment[];
  recurringDeliveries: boolean;
};

export type SellingPlanAllocation = {
  compareAtPrice: number;
  price: number;
  sellingPlan: SellingPlan;
};

export type VariantMetafieldValueResponse<T> = {
  product: {
    variantBySelectedOptions: {
      metafield: {
        value: T;
      } | null;
    } | null;
  } | null;
};

export type ProductVariantOption = {
  description: string;
  image: Image;
  optionName: string;
  optionValue: string;
};

export const imageSchema = z.object({
  alt: z.string().nullable(),
  url: z.string(),
});

export const variantSchema = z.object({
  accentColor: z.string().nullable(),
  available: z.boolean().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  id: z.string(),
  selectedOptions: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
    })
  ),
  image: imageSchema.nullable(),
  images: z.array(imageSchema),
  number_of_loads: z.number().nullable(),
  price: z.number(),
  product: z.object({
    id: z.number(),
  }),
  sellingPlanAllocations: z.array(
    z.object({
      price: z.number().optional().nullable(),
      sellingPlan: z.object({
        id: z.string(),
        name: z.string(),
        priceAdjustments: z.array(
          z.object({
            adjustmentValue: z.object({
              adjustmentPercentage: z.number(),
            }),
          })
        ),
        options: z.array(
          z.object({
            name: z.string(),
            value: z.string(),
          })
        ),
        recurringDeliveries: z.boolean().optional().nullable(),
      }),
    })
  ),
  title: z.string().nullable(),
});
export type VariantState = z.infer<typeof variantSchema>;

export const subscriptionPreferencesSchema = z.object({
  expire_after_specific_number_of_charges: z.number().nullable(),
  interval_unit: z.enum(['day', 'week', 'month']).nullable(),
  order_interval_frequency: z.number().nullable(),
});

export type SubscriptionPreferences = z.infer<
  typeof subscriptionPreferencesSchema
>;

/**
 * Schema based on the Recharge Plan type.
 * It includes just the primary fields we use, so validation results will need to be downcasted to the Plan type.
 * This is to reduce risk that the schema will fail in case Recharge API does not honor its type definitions.
 * Which happens quite often.
 */
export const planSchema = z.object({
  id: z.number(),
  channel_settings: z.object({
    api: z.object({
      display: z.boolean(),
    }),
    checkout_page: z.object({
      display: z.boolean(),
    }),
    customer_portal: z.object({
      display: z.boolean(),
    }),
    merchant_portal: z.object({
      display: z.boolean(),
    }),
  }),
  discount_amount: z.string().nullable(),
  discount_type: z.enum(['percentage', 'fixed_amount', 'shipping']).nullable(),
  external_plan_id: z.union([z.number(), z.string()]).nullable(),
  external_plan_name: z.string().nullable(),
  subscription_preferences: subscriptionPreferencesSchema,
  external_variant_ids: z.array(z.string()).nullable(),
  title: z.string(),
  type: z.enum([
    'prepaid',
    'subscription',
    'onetime',
    'membership_subscription',
  ]),
});

/**
 * Schema for parsing the post_atc_upsell_dialog metaobject
 */
const postAtcUpsellDialogSchema = z.object({
  title: z.string().nullable(),
  body: z.string().nullable(),
  subtitle: z.string().nullable(),
  acceptButtonLabel: z.string().nullable(),
  declineButtonLabel: z.string().nullable(),
  product: z.object({
    id: z.string(),
    featuredImage: imageSchema.nullable(),
    options: z.array(
      z.object({
        name: z.string(),
        values: z.array(z.string()),
      })
    ),
    selectedOrFirstAvailableVariant: variantSchema.nullable(),
    title: z.string(),
    variants: z.array(variantSchema),
  }),
});

export type PostAtcUpsellDialogMetaobject = z.infer<
  typeof postAtcUpsellDialogSchema
>;

export const sellingPlanGroupSchema = z.object({
  app_id: z.string().regex(/^\d+$/).transform(Number),
  selling_plans: z.array(
    z.object({
      id: z.number(),
      name: z.string().nullable(),
      options: z.array(
        z.object({
          name: z.string(),
          value: z.string().nullable(),
        })
      ),
      recurring_deliveries: z.boolean().nullable(),
      price_adjustments: z.array(
        z.object({
          value_type: z.string(),
          value: z.union([z.number(), z.string()]),
          // value: z.number(),
        })
      ),
    })
  ),
});

export type SellingPlanGroup = z.infer<typeof sellingPlanGroupSchema>;

export const productSchema = z.object({
  average_rating: z.number().nullable(),
  id: z.string(),
  featuredImage: imageSchema.nullable(),
  number_of_reviews: z.number().nullable(),
  options: z.array(
    z.object({
      name: z.string(),
      values: z.array(z.string()),
    })
  ),
  selectedOrFirstAvailableVariant: variantSchema.nullable(),
  sellingPlanGroups: z.array(sellingPlanGroupSchema),
  title: z.string(),
  url: z.string().nullable(),
  variants: z.array(variantSchema),
  rechargePlans: z.array(planSchema).nullable(),
  // The name of the 'Pack Size' option is injected via metafield
  // This ensures that it is always in sync with the auto-translated option name
  // when using localized strings
  // Various buy-stack components rely on the values of 'Pack Size' and similar options
  sizeOptionNames: z.array(z.string()).nullable(),
  // Name of the selling plan to use with the first-time-sample feature flag
  samplePackSellingPlan: z.string().nullable(),
  postAtcUpsellDialog: postAtcUpsellDialogSchema.nullable(),
});

export type ProductState = z.infer<typeof productSchema>;

export type ProductSelectionState = {
  monthlyPlans: Plan[];
  onetimePlan: Plan | null;
  packOptions: string[];
  plans: Plan[];
  product: ProductState | null;
  selectedPlan: Plan | null;
  selectedVariant: VariantState | null;
};

export const upsellSettingsSchema = z.object({
  bannerTitle: z.string(),
  title: z.string(),
  buyStackTitle: z.string(),
  buyStackSubtitle: z.string(),
  acceptButtonLabel: z.string(),
  declineButtonLabel: z.string(),
  newPrice: z.number(),
  oldPrice: z.number(),
  variants: z.array(variantSchema),
});

export type UpsellSettings = z.infer<typeof upsellSettingsSchema>;

export const recommendedProductsSchema = z.array(productSchema);
